import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { CoachingClip } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { useThumbnail } from "@/features/coach-review/hooks/useThumbnail";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import {
  useCoachClipMutations,
  useCoachClipsList,
} from "./hooks/useCoachClipsList";
import { PageHeader } from "./components/PageHeader";
import type { GoalComposerSource } from "./components/GoalComposerModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  playerAudienceLabel,
  visibilityLabel,
} from "./feedbackLibraryLabels";

/**
 * Coach > Clips list page (M6a).
 *
 * Ports the legacy `renderCoachClips` (`js/coaching/clips.js`):
 * the team's coaching clips, newest-edited first, each with a
 * thumbnail tile, a `Clip · {category}` meta line, and the
 * per-row action set.
 *
 * Per-row actions:
 *   - **Preview** → opens the M5 `<FeedbackPlayerModal>` in clip
 *     mode (the window-bounded player). The clip carries
 *     `match_id` + `team_id`, so the modal's `match` prop is
 *     built straight from the clip — no extra fetch.
 *   - **Edit** → `/coach/review?clip_id=…` — Coach Review's M5
 *     clip-edit mode (the clip composer lives there).
 *   - **Delete** — confirm + `deleteCoachingClip`.
 *
 * "+ New clip" → `/coach/review?source=video` — Coach Review's
 * video surface, where the coach flips to clip mode and saves a
 * window (the M4→M6 creation-routing carry-in; same `?source=`
 * plumbing the Notes page uses).
 */

export type ClipsPageProps = {
  /** M7.5 D2: opens the goal composer with the clip as source.
   * Undefined ⇒ the row's "+ New goal" button is hidden. */
  onCreatePlayerGoal?: (
    playerId: string,
    source: GoalComposerSource,
  ) => void;
};

export function ClipsPage({ onCreatePlayerGoal }: ClipsPageProps = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const clipsQuery = useCoachClipsList();
  const mutations = useCoachClipMutations();

  // Newest-edited first — legacy sort.
  const clips = useMemo(() => {
    const list = clipsQuery.data?.clips ?? [];
    return [...list].sort((a, b) =>
      String(b.updated_at).localeCompare(String(a.updated_at)),
    );
  }, [clipsQuery.data]);

  const [deleting, setDeleting] = useState<number | null>(null);
  // The clip currently open in the focused-player preview modal.
  const [previewClip, setPreviewClip] = useState<CoachingClip | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CoachingClip | null>(null);

  const handleDelete = (clip: CoachingClip) => {
    setConfirmDelete(clip);
  };

  const confirmDeleteClip = async () => {
    if (!confirmDelete) return;
    const clip = confirmDelete;
    setConfirmDelete(null);
    setDeleting(clip.id);
    try {
      await mutations.deleteClip(clip.id);
      toast.success("Clip deleted");
    } catch (err) {
      toast.error("Couldn't delete clip", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="clips-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete clip?"
          message={`Delete "${confirmDelete.title?.trim() || "this clip"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteClip}
          onClose={() => setConfirmDelete(null)}
          data-testid="clips-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Feedback Clips"
        description={
          <>
            Saved video evidence for player or team feedback. Each row shows
            audience, visibility, source window, and the next coach action.
          </>
        }
        actions={
          <Button
            type="button"
            onClick={() =>
              navigate({ to: "/coach/review", search: { source: "video" } })
            }
            data-testid="clips-new-clip"
            size="sm"
          >
            + New clip
          </Button>
        }
      />

      {clipsQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="clips-loading"
        >
          Loading clips…
        </p>
      ) : clips.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="clips-empty"
        >
          No coaching clips yet. Use <strong>+ New clip</strong> in Coach
          Review to save a video window.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="clips-list">
          {clips.map((clip) => (
            <ClipRow
              key={clip.id}
              clip={clip}
              deleting={deleting === clip.id}
              onPreview={() => setPreviewClip(clip)}
              onEdit={() =>
                navigate({
                  to: "/coach/review",
                  search: { clip_id: clip.id },
                })
              }
              onDelete={() => handleDelete(clip)}
              // M7.5 D2: per-row "+ New goal" — gated on the clip
              // having at least one tagged player (legacy parity).
              onCreateGoal={
                onCreatePlayerGoal && clip.player_ids?.length
                  ? () =>
                      onCreatePlayerGoal(clip.player_ids![0], {
                        source_clip_id: clip.id,
                        seedTitle: clip.title?.trim() || undefined,
                        seedDescription:
                          clip.description?.trim() || undefined,
                      })
                  : undefined
              }
            />
          ))}
        </ul>
      )}

      {/* Focused feedback player — clip mode (M5 modal). The clip
       * carries `match_id` + `team_id`, so the `match` prop is
       * built straight from it. */}
      {previewClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: previewClip }}
          match={{
            id: previewClip.match_id,
            team_id: previewClip.team_id,
          }}
          onClose={() => setPreviewClip(null)}
        />
      ) : null}
    </div>
  );
}

type ClipRowProps = {
  clip: CoachingClip;
  deleting: boolean;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** M7.5 D2: undefined ⇒ no goal button. */
  onCreateGoal?: () => void;
};

function ClipRow({
  clip,
  deleting,
  onPreview,
  onEdit,
  onDelete,
  onCreateGoal,
}: ClipRowProps) {
  const metaParts = [
    playerAudienceLabel(clip.player_ids),
    `Player sees: ${visibilityLabel(clip.visibility)}`,
    `Source: ${formatPlayhead(clip.start_seconds)}-${formatPlayhead(clip.end_seconds)}`,
    `Category: ${clip.category}`,
    "Next: Preview or goal",
  ];
  return (
    <li
      className="flex flex-col items-stretch gap-3 rounded border border-rule bg-surface px-3 py-2 shadow-sm sm:flex-row sm:items-center"
      data-testid="clip-row"
      data-clip-id={clip.id}
    >
      <ClipThumb clipId={clip.id} />
      <div className="min-w-0 flex-1">
        <div className="font-display text-ink truncate">
          {clip.title?.trim() || "(untitled clip)"}
        </div>
        <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
          {metaParts.join(" · ")}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 sm:justify-end">
        <RowButton
          label="Preview"
          testid="clip-action-preview"
          onClick={onPreview}
        />
        <RowButton label="Edit" testid="clip-action-edit" onClick={onEdit} />
        {/* M7.5 D2: per-row "+ New goal", gated on the clip
         * having a tagged player. */}
        {onCreateGoal ? (
          <RowButton
            label="+ New goal"
            testid="clip-action-goal"
            onClick={onCreateGoal}
          />
        ) : null}
        <RowButton
          label="Delete"
          testid="clip-action-delete"
          onClick={onDelete}
          disabled={deleting}
          danger
        />
      </div>
    </li>
  );
}

/** Clip thumbnail tile — the access-controlled JPEG via the M5
 * `useThumbnail` primitive; film-strip placeholder until it
 * resolves / on failure. */
function ClipThumb({ clipId }: { clipId: number }) {
  const url = useThumbnail("clip", clipId);
  if (!url) {
    return (
      <span
        className="block h-11 w-16 shrink-0 rounded bg-gradient-to-br from-canvas to-surface border border-rule/60"
        aria-hidden="true"
        data-testid="clip-thumb-placeholder"
      />
    );
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      data-testid="clip-thumb"
      className="block h-11 w-16 shrink-0 rounded object-cover border border-rule/60"
    />
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "rounded border px-2 py-1 text-xs transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive"
          : "border-rule text-ink-muted hover:text-ink hover:bg-field",
      ].join(" ")}
    >
      {props.label}
    </button>
  );
}
